<template>
	<div class="w-full p-2 md:p-8 flex items-center">
		<slot
			:logout="logout"
			:login="login"
			:email="email"
			:password="password"
			:createAccount="createAccount"
			:sendRecoveryEmail="sendRecoveryEmail"
			:resetPassword="resetPassword"
		>
			<form @submit.stop.prevent class="rounded-lg flex flex-col space-y-4 w-full">
				<template v-if="creatingAccount">
					<div class="flex w-full justify-center items-center md:pt-0">
						<div class="w-full max-w-5xl">
							<h1 class="flex w-full justify-center text-xl md:text-3xl font-semibold text-ihh-midnight-gray pb-10">New Account</h1>
							<div class="flex w-full justify-center gap-x-4">
								<div class="grid grid-cols-1 sm:grid-cols-2 place-items-center w-full lg:max-w-5xl gap-x-8">
									<div v-for="(field, key) in user" :key="key" class="w-full text-ihh-gray-700 font-medium">
										<div v-if="field.type === 'date'">
											<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
											<div class="h-10 mb-3">
												<input v-model="field.value" :id="key" :type="field.type" class="pl-1 w-full h-12 text-gray-400 font-normal" />
											</div>
										</div>
										<div v-else-if="key === 'hcProvider'">
											<div>
												<label :for="key" class="font-semibold text-sm">{{ field.display }}</label>
											</div>
											<div class="inline-flex ml-1 items-center">
												<input v-model="field.value" :id="key" :type="field.type" class="" />
												<span class="ml-2">{{ field.details }}</span>
											</div>
										</div>
										<div v-else-if="key === 'smsConsent'">
											<div>
												<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
											</div>
											<div class="inline-flex ml-1 items-center">
												<input class="h-12" v-model="field.value" :id="key" :type="field.type" />
												<span class="ml-2">{{ field.details }}</span>
											</div>
										</div>
										<div v-else-if="key === 'password1'">
											<div class="flex justify-between">
												<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
												<span v-if="field.required" class="font-semibold uppercase text-sm">Required</span>
											</div>
											<div class="h-10 mb-3">
												<input v-model="field.value" :id="key" :type="field.type" class="pl-1 w-full h-12 text-gray-400 font-normal" />
											</div>
											<!-- <span class="text-red-600 text-sm font-normal">{{ passwordComplexityError }}</span> -->
										</div>
										<div v-else-if="key === 'password2'">
											<div class="flex justify-between">
												<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
												<span v-if="field.required" class="font-semibold uppercase text-sm">Required</span>
											</div>
											<div class="h-10 mb-3">
												<input v-model="field.value" :id="key" :type="field.type" class="pl-1 w-full h-12 text-gray-400 font-normal" />
												<!-- <p class="text-red-600 text-sm font-normal">{{ passwordMatchError }}</p> -->
											</div>
										</div>
										<div v-else-if="field.type !== 'select'">
											<div class="flex justify-between">
												<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
												<span v-if="field.required" class="font-semibold uppercase text-sm">Required</span>
											</div>
											<div class="h-10 mb-3">
												<div class="w-full flex items-center">
													<input v-model="field.value" :id="key" :type="field.type" class="w-full h-12 font-normal pl-1" />
												</div>
											</div>
										</div>
										<div v-else-if="field.display === 'Country'">
											<div class="flex justify-between">
												<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
												<span v-if="field.required" class="font-semibold uppercase text-sm">Required</span>
											</div>
											<div>
												<select v-model="user.country.value" @change="fetchStates" :id="key" class="h-10 w-full mb-3 text-gray-400 font-normal h-12">
													<option value="" disabled selected>Select Country</option>
													<option v-for="country in countries" :key="country.isoCode" :value="country.isoCode">{{ country.name }}</option>
												</select>
											</div>
										</div>
										<div v-else-if="field.display === 'State'">
											<div class="flex justify-between">
												<label class="font-semibold text-sm" :for="key">{{ field.display }}</label>
												<span v-if="field.required" class="font-semibold uppercase text-sm">Required</span>
											</div>
											<div>
												<select v-model="user.state.value" :id="key" class="h-10 w-full h-12 mb-3 text-gray-400 font-normal">
													<option disabled value="">Select State</option>
													<option v-for="state in states" :key="state.name" :value="state.name">{{ state.name }}</option>
												</select>
											</div>
										</div>
										<div v-else-if="key === 'addressType'">
											<label :for="key" class="font-semibold text-sm">{{ field.display }}</label>
											<div class="flex flex-wrap">
												<div v-for="option in field.options" :key="option" class="flex items-center mb-3 mr-4">
													<input type="radio" :id="option" :value="option" v-model="field.value" class="mr-1 h-12" />
													<label :for="option" class="text-gray-400 font-normal">{{ option }}</label>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div>
						<div class="flex justify-center">
							<!-- change button color -->
							<button
								@click.stop.prevent="action"
								class="w-full xs:w-fit text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
							>
								<template v-if="!processing">{{ buttonText }}</template>
								<Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
							</button>
						</div>

						<button v-if="store.user?.id" @click="logout" class="text-xs text-gray-400 hover:underline">Logout</button>

						<div class="text-center select-none mt-2">
							<!-- <button v-if="!creatingAccount"     @click="creatingAccount = true; guest = false; forgotPassword = false" class="text-xs text-gray-400 hover:underline">Create Account</button> -->
							<button
								@click.stop.prevent="toLogin"
								class="text-xs text-gray-400 hover:underline"
								:class="{ 'ml-2 pl-2 border-l dark:border-gray-500': resettingPassword }"
							>
								{{ resettingPassword || (allowGuest && !guest) ? 'Login' : 'Have an account? Login instead.' }}
							</button>
							<!-- <button v-if="allowGuest && !guest" @click="creatingAccount = true; guest = true; forgotPassword = false" class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500">Continue as Guest</button> -->
							<!-- <button v-if="!creatingAccount && !resettingPassword"     @click="creatingAccount = false; guest = false; forgotPassword = true" class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500">Forgot Password?</button> -->
						</div>
					</div>
				</template>

				<!-- <template v-else-if="forgotPassword">
                    <div class="w-full">
                        <div class="w-7/8">
                            <div class="flex justify-center">
                                <div class="flex flex-col w-1/2 mb-8 p-4 rounded-lg shadow-lg bg-gradient-to-b from-ihh-pink to-ihh-purple pb-8">
                                    <h3 class="flex justify-center text-xl md:text-2xl font-semibold tracking-wide text-ihh-midnight-gray pt-8">Recover Password</h3>
                                    <div class="w-full">
                                        <div class="my-6 md:max-w-md mx-auto">
                                            <div class="text-sm text-ihh-gray-600 mb-1">Email Address:</div>
                                            <input
                                                v-model="email"
                                                type="text"
                                                required
                                                class="w-96 py-3 pl-4 flex border border-ihh-gray-600 rounded text-sm md:text-base bg-white font-light text-black placeholder-ihh-gray relative"
                                            />
                                        </div>
                                        <div v-if="!creatingAccount" class="my-6 md:max-w-md mx-auto ">
                                            <div class="flex justify-center">
                                                <button @click="action" class="w-48 px-3 py-2 bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide dark:text-white/75 text-white font-normal rounded flex justify-center">
                                                    <template v-if="!processing">{{ buttonText }}</template>
                                                    <Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template> -->

				<template v-else>
					<div class="w-full">
						<!-- beginning of the sign-in/login that I created -->

						<div class="flex w-full justify-center text-xl md:text-3xl font-semibold tracking-wide text-ihh-midnight-gray pb-5">
							{{ forgotPassword ? 'Recover Password' : resettingPassword ? 'Update Password' : 'Sign In' }}
						</div>
						<div class="flex w-full justify-center">
							<div class="w-full max-w-4xl">
								<div class="flex w-full" :class="{ 'grid grid-cols-1 md:grid-cols-2 gap-x-6 justify-around': !small }">
									<div class="flex w-full justify-center items-center text-4xl font-semibold">
										<div class="flex w-full justify-center gap-y-6 flex-col items-center my-8 px-6 md:px-0">
											<div class="relative w-full md:max-w-md" v-if="!resettingPassword">
												<div class="text-sm text-ihh-gray-600 mb-2">Email Address:</div>
												<input
													v-model="email"
													type="text"
													required
													class="py-3 pl-4 flex border border-ihh-gray-600 rounded w-full text-sm md:text-base bg-white font-light text-black placeholder-ihh-gray relative"
												/>
											</div>
											<div class="relative w-full md:max-w-md" v-if="!forgotPassword">
												<div class="text-sm text-ihh-gray-600 mb-2">Password:</div>
												<input
													v-model="password"
													type="password"
													required
													class="py-3 pl-4 flex border border-ihh-gray-600 rounded w-full text-sm md:text-base bg-white font-light text-black placeholder-ihh-gray relative"
												/>
											</div>

											<div v-if="!creatingAccount" class="my-4 md:max-w-md">
												<div class="flex justify-center">
													<button
														@click.stop.prevent="action"
														class="w-full xs:w-fit text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
													>
														<template v-if="!processing">{{ buttonText }}</template>
														<Spinner v-else class="border-t-family-at-church-orange w-4 h-4 my-1" :small="true" color="border-t-white/50" />
													</button>
												</div>

												<button v-if="store.user?.id" @click="logout" class="text-xs text-gray-400 hover:underline">Logout</button>

												<div class="text-center select-none">
													<button v-if="!creatingAccount" @click="handleCreateAccount" class="text-xs text-gray-400 hover:underline">
														Create Account
													</button>
													<button
														v-if="creatingAccount || resettingPassword"
														@click="toLogin"
														class="text-xs text-gray-400 hover:underline"
														:class="{ 'ml-2 pl-2 border-l dark:border-gray-500': resettingPassword }"
													>
														{{ resettingPassword || (allowGuest && !guest) ? 'Login' : 'Have an account? Login instead.' }}
													</button>
													<button
														v-if="allowGuest && !guest"
														@click="handleGuestContinue"
														class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500"
													>
														Continue as Guest
													</button>
													<button
														v-if="!creatingAccount && !resettingPassword && !forgotPassword"
														@click="toForgotPassword"
														class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500"
													>
														Forgot Password?
													</button>
													<button
														v-if="forgotPassword"
														@click="forgotPassword = false"
														class="text-xs text-gray-400 hover:underline ml-2 pl-2 border-l dark:border-gray-500"
													>
														Return to Login
													</button>
												</div>
											</div>
										</div>
									</div>

									<div v-if="!small" class="flex flex-col items-center md:block">
										<div class="p-6 md:p-12 w-full justify-end bg-ihh-light-lavendar rounded-xl shadow-md max-w-sm md:max-w-md">
											<div class="text-xl font-semibold tracking-wide text-ihh-midnight-gray">New Here?</div>
											<div class="py-4">
												<div class="text-base text-ihh-gray-600">Create an account with us and you'll be able to:</div>
												<ul class="text-ihh-gray-600 list-disc ml-12 font-normal">
													<li class="text-normal font-thin">Check out faster</li>
													<li class="text-normal font-thin">Save multiple shipping addresses</li>
													<li class="text-normal font-thin">Access your order history</li>
													<li class="text-normal font-thin">Track new orders</li>
													<li class="text-normal font-thin">Save items to your Wish List</li>
												</ul>
												<div class="mt-4 w-full flex justify-center">
													<button
														@click="handleCreateAccount"
														class="flex md:w-fit text-center justify-center md:justify-start text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
													>
														Create Account
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="w-full flex flex-col items-center md:items-start mt-8 md:mt-0 mb-24">
									<div v-if="!small" class="flex max-w-sm md:max-w-lg md:w-1/2 justify-center">
										<div class="p-10 bg-ihh-light-lavendar w-full rounded-xl shadow-md md:max-w-md">
											<div class="text-xl font-semibold tracking-wide text-ihh-midnight-gray">Are you a Midwife?</div>
											<div class="py-4">
												<div class="text-base text-ihh-gray-600">Setup your birth kit with us:</div>
												<ul class="text-ihh-gray-600 list-disc ml-12 font-normal">
													<li class="text-normal font-thin">Wide array of birth items to choose from</li>
													<li class="text-normal font-thin">Fully customizable</li>
													<li class="text-normal font-thin">Personalized page on our website</li>
													<li class="text-normal font-thin">Birth kits shipped directly to your clients home</li>
												</ul>
												<div class="mt-4 w-full flex justify-center">
													<button
														class="flex md:w-fit text-center justify-center md:justify-start text-sm md:text-base font-semibold text-ihh-midnight-gray py-3 px-10 rounded-lg bg-ihh-dark-salmon hover:bg-ihh-salmon delay-100 transition tracking-wide"
													>
														<router-link to="/new-birth-kit-setup">Setup custom birth kit</router-link>
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<!-- end of the sign-in/login that I created -->
					</div>
				</template>
			</form>
		</slot>
	</div>
</template>

<script>
import axios from 'axios'
import { Country, State } from 'country-state-city'
import Swal from 'sweetalert2'
import { validateEmail } from '../../helpers/email'
import { getUser } from '../../helpers/user'
import { useCheckoutStore } from '../../stores/store.js'
import Mango from '../../helpers/mango'
import { user } from '../../helpers/newUser'

// Function for setting cookies
let setCookie = function (cname, cvalue) {
	var d = new Date()
	d.setTime(d.getTime() + 365 * 24 * 60 * 60 * 1000)
	var expires = 'expires=' + d.toUTCString()
	document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/'
}

export default {
	props: ['small'],
	inject: ['store', 'axios'],
	emits: ['loggedIn', 'hide'],
	data() {
		return {
			checkout: useCheckoutStore(),
			email: null,
			password: null,
			user,
			countries: [],
			states: [],
			processing: false,
			creatingAccount: false,
			guest: false,
			forgotPassword: false,
			passwordMatchError: null,
			passwordComplexityError: null,
		}
	},
	watch: {
		loggedIn() {
			this.$emit('hide')
		},
	},
	methods: {
		toLogin() {
			this.creatingAccount = false
			this.guest = false
			this.forgotPassword = false
			this.$router.push('/login')
		},
		toForgotPassword() {
			this.creatingAccount = false
			this.guest = false
			this.forgotPassword = true
		},
		handleCreateAccount() {
			this.creatingAccount = true
			this.guest = false
			this.forgotPassword = false
			this.$router.push('/register')
		},
		handleGuestContinue() {
			this.creatingAccount = true
			this.guest = true
			this.forgotPassword = false
			this.$router.push('/register')
		},
		validateEmail,
		fetchCountries() {
			this.countries = Country.getAllCountries()
		},
		fetchStates() {
			if (this.user.country.value) {
				this.states = State.getStatesOfCountry(this.user.country.value)
			}
		},
		validatePasswordComplexity(password) {
			const minLength = 7
			const hasAlphabetic = /[a-zA-Z]/.test(password)
			const hasNumeric = /[0-9]/.test(password)

			if (password.length < minLength) {
				this.passwordComplexityError = 'Password must be at least 7 characters long.'
			} else if (!hasAlphabetic) {
				this.passwordComplexityError = 'Password must contain at least one alphabetic character.'
			} else if (!hasNumeric) {
				this.passwordComplexityError = 'Password must contain at least one numeric character.'
			} else {
				this.passwordComplexityError = null
			}
		},
		validatePasswordsMatch() {
			if (this.user.password1.value !== this.user.password2.value) {
				this.passwordMatchError = 'Passwords do not match'
			} else {
				this.passwordMatchError = null
			}
		},
		validateForm() {
			const requiredFields = ['email', 'password1', 'password2', 'firstName', 'lastName', 'street', 'city', 'state', 'zip', 'country', 'phone']

			for (const field of requiredFields) {
				if (!this.user[field].value) {
					return Swal.fire({
						icon: 'error',
						title: `${this.user[field].display} is required`,
						text: `Please fill out the ${this.user[field].display} field`,
					})
				}
			}

			this.validatePasswordsMatch()
			this.validatePasswordComplexity(this.user.password1.value)

			if (this.passwordMatchError) {
				Swal.fire({
					icon: 'error',
					title: 'Passwords do not match',
					text: 'Please make sure your passwords match',
				})
				return { success: false }
			} else if (this.passwordComplexityError) {
				Swal.fire({
					icon: 'error',
					title: 'Password complexity error',
					text: this.passwordComplexityError,
				})
				return { success: false }
			}

			return { success: true }
		},
		async login(email, password) {
			email = this.email || email
			password = this.password || password

			console.log('email typeof: ', typeof email)
			console.log('email: ', email)
			// Check if email is an event object (MouseEvent, PointerEvent, etc.)
			// This happens when the login method is called directly from a button click
			if (email instanceof Event || (typeof email === 'object' && email.type === 'click')) {
				email = this.email
				password = this.password
			}

			let member = {}
			this.processing = true

			try {
				try {
					member = await Mango.login({ email, password })
				} catch (error) {
					console.log('Error: ', error)
				}

				if (member?.success && member?.memberId) {
					if (member.roles?.includes('admin')) member.admin = true

					window.localStorage.setItem('user', member.memberId)
					window.localStorage.setItem('token', member.token)
					window.localStorage.setItem('email', email)

					this.checkout.resetGuest()
					this.checkout.resetShippingAddress()
					this.checkout.resetBillingAddress()
					this.checkout.clearAddresses()
					window.sessionStorage.removeItem('addresses')
					window.sessionStorage.removeItem('guest')
					window.sessionStorage.removeItem('shipping')
					window.sessionStorage.removeItem('billing')

					setCookie(`Authorization`, `${member.token}`)
					this.axios.defaults.headers.common['Authorization'] = `${member.token}`

					// const user = await getUser();
					// this.store.user = user;

					// Merge recenlty viewed items
					if (this.store?.recentlyViewed?.length) {
						let items = this.store.user.recentlyViewed || []
						items = items.concat(this.store.recentlyViewed)
						if (items.length > 10) {
							items = items.slice(items.length - 10)
						}
						await Mango.members.save({ id: this.store.user.id, recentlyViewed: items })
						this.store.recentlyViewed = []
						// store.user will be set to the Mango user again in initUserCart() below.
						// This will update the store.user.recentlyViewed
					}

					// Note, this initUserCart() is resettting the store user to the Mango user (needs to happen after
					// merging recently viewed above) AND it is reinitializing the store cart (required to pass the
					// store.user into useCart again)
					await this.store.initUserCart()

					// Handle redirection and completion
					this.$emit('loggedIn')
					this.$emit('hide') // FIXED the issue with login modal not closing after successful login by adding this

					console.log('this.$route.path: ', this.$route.path)

					if (this.$route.path === '/checkout' || this.small) {
						window.location.reload()
						return
					} else {
						console.log('Redirecting to Home')
						let restrictedPath = this.store?.login?.next || '/'
						if (restrictedPath) this.$router.push(restrictedPath)
					}
				} else if (member?.invalidFields || !member?.success) {
					console.log('member.Id not returned')
					console.log(member)
					Swal.fire({
						title: `Invalid ${member?.invalidFields.join(', ')}`,
						icon: 'error',
						//text: member.errors
					})
				}
			} catch (error) {
				console.error('Error authenticating to Mango', error)
			}

			this.processing = false
		},
		async logout() {
			// Retrieve the cart object from localStorage
			let cart = JSON.parse(window.localStorage.getItem('cart'))
			if (cart) {
				cart.id = null
				window.localStorage.setItem('cart', JSON.stringify(cart))
			}
			this.store.cart.id = null

			this.store.showBag = false

			this.checkout.resetGuest()
			this.checkout.clearAddresses()
			this.checkout.resetShippingAddress()
			this.checkout.resetBillingAddress()
			window.sessionStorage.removeItem('shipping')
			window.sessionStorage.removeItem('addresses')
			window.sessionStorage.removeItem('billing')
			window.sessionStorage.removeItem('guest')

			this.store.user = {}
			window.user = null
			window.localStorage.removeItem('user')
			window.localStorage.removeItem('token')
			window.localStorage.removeItem('email')
			window.localStorage.removeItem('auth')
			window.localStorage.removeItem('recentlyViewed')
			setCookie(`Authorization`, ``)
			delete this.axios.defaults.headers.common['Authorization']
			this.$emit('loggedOut')
			this.$emit('hide')

			if (this.$route.path === '/checkout') {
				window.location.reload()
			} else {
				this.$router.push('/login')
			}

			await this.store.initUserCart()
		},
		async createAccount() {
			if (!this.validateForm().success) return

			const data = {
				email: this.user.email.value,
				firstName: this.user.firstName.value,
				lastName: this.user.lastName.value,
				company: this.user.company.value,
				phone: this.user.phone.value,
				address1: this.user.street.value,
				address2: this.user.apt.value,
				city: this.user.city.value,
				country: this.user.country.value,
				state: this.user.state.value,
				zip: this.user.zip.value,
				addressType: this.user.addressType.value,
				password: this.user.password1.value,
				smsConsent: this.user.smsConsent.value,
				dueDate: this.user.dueDate.value,
				hcProvider: this.user.hcProvider.value,
			}

			try {
				let response = await axios.post(`${this.store.api}/controllers/account/create`, data)

				console.log(response.data.id)

				if (!response.data.id) {
					return Swal.fire({
						icon: 'error',
						title: 'Error creating account',
						text: 'Please try again',
					})
				} else if (response.data.id) {
					let member = await Mango.login({ email: data.email, password: data.password })

					if (member?.memberId) {
						if (member.roles?.includes('admin')) member.admin = true

						window.localStorage.setItem('user', member.memberId)
						window.localStorage.setItem('token', member.token)
						window.localStorage.setItem('email', this.user.email)

						setCookie(`Authorization`, `${member.token}`)
						this.axios.defaults.headers.common['Authorization'] = `${member.token}`

						const user = await getUser()
						this.store.user = user

						// Handle redirection and completion
						this.$emit('loggedIn')
						this.$emit('hide') // FIXED the issue with login modal not closing after successful login by adding this

						let restrictedPath = this.store?.login?.next || '/'
						if (restrictedPath) this.$router.push(restrictedPath)
					} else if (member?.invalidFields) {
						Swal.fire({ title: `Invalid ${member.invalidFields.join(', ')}`, icon: 'error' })
					}

					this.$emit('accountCreated')
					this.$emit('hide')

					this.$router.push({ path: '/welcome', query: { email: this.user.email.value } })
					this.processing = false
				}
			} catch (error) {
				console.error('Error creating account', error)
			}
		},
		async sendRecoveryEmail() {
			let email = this.email
			this.processing = true
			let response = await this.axios.post(`${this.store.api}/controllers/account/sendResetInstructions`, { email, forgot: true })
			if (response.data.success) {
				// Swal.fire('Success!', `Recovery instructions sent to: ${this.email}<br><br>If you don't receive an email within a couple minutes, please check your junk folder.`, 'success')
				Swal.fire('Success!', `Recovery instructions sent to: ${this.email}`, 'success')
				this.forgotPassword = false
			} else {
				Swal.fire('Invalid Email!', `The following user does not exist: ${this.email}`, 'warning')
			}
			this.$emit('emailSent')
			this.$emit('hide')
			this.processing = false
		},
		async resetPassword() {
			this.validatePasswordComplexity(this.password)
			if (this.passwordComplexityError) {
				return Swal.fire({
					icon: 'error',
					title: 'Password Complexity Error',
					text: this.passwordComplexityError,
				})
			}
			this.processing = true

			let data = {
				email: this.$route.query.email,
				salt: this.$route.query.salt,
				password: this.password,
			}

			let response = await this.axios.post(`${this.store.api}/controllers/account/resetPassword`, data)
			console.log(response.data)
			if (response.data.success) {
				this.email = this.$route.query.email
				console.log('Logging in after password reset')
				console.log('Email: ', this.email)
				console.log('Password: ', this.password)
				await this.login()
				this.$router.push('/account')
				Swal('Success!', 'Your password has been reset.', 'success')
			} else {
				Swal('Invalid Link!', `The link you're using is invalid.`, 'error')
			}

			this.processing = false
			this.$emit('passwordReset')
			this.$emit('hide')
		},
	},
	computed: {
		guestPassword() {
			return Math.random().toString(36).substr(2) + Math.random().toString(36).substr(2)
		},
		labelText() {
			return this.resettingPassword
				? 'Reset Password'
				: this.forgotPassword
					? 'Recover Password'
					: this.creatingAccount
						? this.guest
							? 'Continue as Guest'
							: 'Create Account'
						: 'Login'
		},
		buttonText() {
			return this.resettingPassword
				? 'Update Password'
				: this.forgotPassword
					? 'Send Recovery Email'
					: this.creatingAccount
						? this.guest
							? 'Continue'
							: 'Create Account'
						: 'Login'
		},
		action() {
			return this.resettingPassword ? this.resetPassword : this.forgotPassword ? this.sendRecoveryEmail : this.creatingAccount ? this.createAccount : this.login
		},
		resettingPassword() {
			return !!this.$route.query?.salt
		},
		loggedIn() {
			return !!this.store.user?.id
		},
	},
	created() {
		this.fetchCountries()
	},
	mounted() {
		if (this.$route?.query?.resetPassword === 'true') {
			this.forgotPassword = true
		}

		if (this.$route?.query?.createAccount === 'true') {
			this.creatingAccount = true
		}

		if (this.$route?.path === '/register') {
			this.creatingAccount = true
		}
	},
	watch: {
		'user.password1.value': function (newValue) {
			this.validatePasswordComplexity(newValue)
		},
		'user.password2.value': function () {
			this.validatePasswordsMatch()
		},
	},
}
</script>

<style lang="postcss" scoped>
input,
select {
	@apply border border-gray-700 rounded px-3 py-2;
}
</style>
